import type { Membership } from '../../../lib/api';

/**
 * Per-tier Binary daily cycle limits (Legacy + Extreme variants). Stored on each tier as
 * `config.binary_cycle_limits.{daily_cap,max_pairs_per_day,max_strong_leg}` —
 * each, when set, overrides the plan-level value for THIS tier's receiver so a
 * higher tier can pair/earn more than a lower one. Blank / 0 = use the plan
 * default. `daily_cap` is binary's self-managed daily income cap (distinct from
 * the universal `daily_caps` and the Teslab `distribution_caps`).
 */
const FIELDS = [
  { key: 'daily_cap', label: 'Daily income cap', placeholder: 'plan default' },
  { key: 'max_pairs_per_day', label: 'Max pairs / day', placeholder: 'plan default' },
  { key: 'max_strong_leg', label: 'Max strong leg', placeholder: 'plan default' },
] as const;

export function BinaryCycleLimitsPerTier({
  memberships,
  draft,
  onChange,
  readOnly,
}: {
  memberships: Membership[];
  draft: Record<string, Record<string, unknown>>;
  onChange: (id: string, next: Record<string, unknown>) => void;
  readOnly: boolean;
}) {
  function getVal(cfg: Record<string, unknown>, key: string): string {
    const bcl = cfg.binary_cycle_limits as Record<string, unknown> | undefined;
    const v = bcl?.[key];
    return typeof v === 'number' || typeof v === 'string' ? String(v) : '';
  }
  function setVal(id: string, key: string, raw: string) {
    const current = draft[id] ?? {};
    const bcl = { ...((current.binary_cycle_limits as Record<string, unknown> | undefined) ?? {}) };
    const n = Number(raw);
    if (raw.trim() === '' || Number.isNaN(n) || n <= 0) delete bcl[key];
    else bcl[key] = n;
    const next = { ...current };
    if (Object.keys(bcl).length === 0) delete next.binary_cycle_limits;
    else next.binary_cycle_limits = bcl;
    onChange(id, next);
  }

  return (
    <div className="space-y-2">
      <p className="text-[10px] text-muted-foreground">
        Per-tier overrides for the Binary daily income cap, paid-pairs-per-day, and
        strong-leg PV cap. Blank / 0 = use the plan default. Higher tiers can set larger limits than
        lower tiers.
      </p>
      <div className="overflow-x-auto border rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-left text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Tier</th>
              {FIELDS.map((f) => (
                <th key={f.key} className="px-3 py-2 font-medium">
                  {f.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {memberships.map((m) => (
              <tr key={m.id} className="border-t">
                <td className="px-3 py-1.5 text-xs font-mono" title={m.name}>
                  {m.code}
                </td>
                {FIELDS.map((f) => (
                  <td key={f.key} className="px-2 py-1.5">
                    <input
                      type="number"
                      className="h-8 w-28 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                      value={getVal(draft[m.id] ?? {}, f.key)}
                      disabled={readOnly}
                      step="any"
                      min={0}
                      placeholder={f.placeholder}
                      onChange={(e) => setVal(m.id, f.key, e.target.value)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
